/* eslint-disable */
import {inject, Injectable} from '@angular/core';
import {CustomDimensionsMap, DimensionTypes, EventMap, CreateTrackPageViewArgs, CreateTrackEventArgs} from "./actions";
import {
  CustomDimensionsService,
  CustomEventsService,
  DataLayerService,
  PageViewsService,
  UserManagementService,
} from "@piwikpro/ngx-piwik-pro";
import {VisitorInfo} from '@piwikpro/tracking-base-library';
import {
  DeepSignalValue,
  DemographicsInfo, InsuranceInfo,
  MetaInfo,
  PageStepsMap, SchedulerDataSnapshot,
  SearchInfo,
  SelectedAppointmentInfo
} from "../../statics";
import {PromiseTimeout, Sleep} from "@mindpath/shared";
import {AppData} from "../../services/app.service";
import {DataService} from "../../services/data.service";
import {GenderIdMapping, LanguageIdMapping} from 'src/app/services/finder.service';

export interface PiwikUserInfo {
  userId: string;
  visitorId: string;
  visitorInfo: VisitorInfo;
}

@Injectable({
  providedIn: 'root'
})
export class PiwikService {

  private pageView = inject(PageViewsService);
  private customDimensions = inject(CustomDimensionsService);
  public customEvents = inject(CustomEventsService);
  private userManagement = inject(UserManagementService);

  readonly tryReadUserInfo = this.#tryLoad();

  constructor() {
    (window as any).piwik = this;
  }


  /*
    Piwik's getmethods are promises. But they never reject if there is not a user it's more of a "wait" until there is a user.
    So because we set the X-Piwik-Session header in the interceptor we can't just do the normal call and wait because
    it's possible that the call will never resolve if there is no user. So we have to do a little dance to make sure
    that we get the user and fail after a reasonable amount of time if it doesn't resolve.
    It also sets up reattempts in the background if it fails. So that we might hopefully get the user later for subsequent calls.
   */
  #tryLoad() {
    const maxRetryCount = 10;
    const maxTimeout = 1500;
    const retryDelay = 150;
    const fullReAttemptInterval = 10_000; // 10 seconds

    let info: Promise<PiwikUserInfo | null> | null = null;

    const innerTry = async (): Promise<PiwikUserInfo | null> => {
      try {
        return PromiseTimeout(this.getInfo(), maxTimeout);
         
      } catch (e) {
        return null;
      }
    }

    const fireBackgroundRetries = async (resolve: (value: PiwikUserInfo | null) => void) => {
      // retry maxRetryCount times
      for (let i = 0; i < maxRetryCount; i++) {
        try {
          // reset the promise
          resolve(await innerTry());
          break;
           
        } catch (e) {
          resolve(null);
          await Sleep(retryDelay);
        }
      }

      // enable a full reattempt in fullReAttemptInterval ms
      setTimeout(() => {
        info = null;
      }, fullReAttemptInterval);

    }

    return async () => {

      // if we don't have a promise, create one
      if (!info) {
        const {promise, resolve} = Promise.withResolvers<PiwikUserInfo | null>();
        info = promise;
        try {
          // try to get the info
          resolve(await innerTry());
        } catch (e) {
          // if it fails, fire the retries in the background and resolve with null
          console.debug('Error getting piwik info', e);
          fireBackgroundRetries(resolve).catch(e => console.error('Error firing background retries', e));
          resolve(null);
        }
      }
      return info;
    };
  }

  async getInfo(): Promise<PiwikUserInfo> {
    const userId = await this.userManagement.getUserId();
    const visitorId = await this.userManagement.getVisitorId();
    const visitorInfo = await this.userManagement.getVisitorInfo();
    return {userId, visitorId, visitorInfo};
  }

  trackEvent<TSection extends keyof EventMap, TEvent extends keyof EventMap[TSection]>(section: TSection, event: TEvent, data: EventMap[TSection][TEvent]) {
    try {
      this.customEvents.trackEvent(...CreateTrackEventArgs(section, event, data));
    } catch (e) {
      console.error('Error tracking event', e, {section, event, data});
    }
  }

  trackPage<TSection extends keyof PageStepsMap>(section: TSection, step: PageStepsMap[TSection]) {
    try {
      this.pageView.trackPageView(CreateTrackPageViewArgs(section, step));
    } catch (e) {
      console.error('Error tracking page', e, {section, step});
    }
  }

  trackDimension<TDim extends keyof CustomDimensionsMap>(dimension: TDim, value: CustomDimensionsMap[TDim]) {
    try {
      this.customDimensions.setCustomDimensionValue(dimension, typeof value === 'string' ? value : JSON.stringify(value));
    } catch (e) {
      console.error('Error tracking dimension', e, {dimension, value});
    }
  }

  #dataSvc = inject(DataService);
  async trackDimensions(data: AppData) {
    try {
      const state = data.demographics?.address?.state;
      const typeOfCare = data.appt?.typeOfCare ?? data.filters.typeOfCare;
      const age = data.filters.ageRange;
      const setting = data.appt?.chosenSetting ?? data.filters.setting;
      const insurance = data.insurance?.primary?.id ?? 0;
      const plan = data.insurance?.primary?.planId;
      const lat = data.demographics?.address?.coordinates?.lat;
      const long = data.demographics?.address?.coordinates?.long;
      let condition = data.appt?.conditionId ?? data.filters.conditionId;
      const genderId = data.filters.genderId;
      const languageId = data.filters.languageId;

      let insuranceName: string | undefined = undefined;
      let planName: string | undefined = undefined;
      if (insurance > 0) {
        const insuranceItem = await this.#dataSvc.getInsurer(insurance);
        insuranceName = insuranceItem?.name ?? insurance.toString();
        if (plan) {
          planName = insuranceItem?.plans?.filter(p => p.id === plan)[0]?.name ?? plan.toString();
        }
      }

      let conditionName: string | undefined = undefined;
      if (condition) {
        if (condition === -1) {
          conditionName = 'General Evaluation';
        } else {
          const conditions = await this.#dataSvc.getConditions();
          conditionName = conditions.filter(c => c.id === condition)[0]?.name ?? condition.toString();
        }
      }

      const dimensions = {
        [DimensionTypes.State]: state,
        [DimensionTypes.TypeOfCare]: typeOfCare,
        [DimensionTypes.Age]: age,
        [DimensionTypes.ApptSettingFilter]: setting,
        [DimensionTypes.Insurance]: insuranceName,
        [DimensionTypes.Plan]: planName,
        [DimensionTypes.Latitude]: lat?.toString(),
        [DimensionTypes.Longitude]: long?.toString(),
        [DimensionTypes.Condition]: conditionName,
        [DimensionTypes.GenderFilter]: Object.entries(GenderIdMapping).find(([, value]) => value === genderId)?.[0],
        [DimensionTypes.LanguageFilter]: Object.entries(LanguageIdMapping).find(([, value]) => value === languageId)?.[0],
      };

      Object.entries(dimensions).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          this.customDimensions.setCustomDimensionValue(key, value);
        } else {
          this.customDimensions.deleteCustomDimension(key);
        }
      });
    } catch (e) {
      console.error('Error tracking dimensions', e);
    }
  }
}
